/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import {
  AssetSortType,
  AssetType,
  DataProvider,
  getPrimaryLanguage,
  IMediaModel,
} from "@nf/common";
import { VALID_COUNTRY } from "@nf/constants";
import { ISitemapField } from "next-sitemap";

import { RouteHelper } from "helpers";

const MAX_PAGE_SIZE = 200;
const LIST_SIZE = 800;

export class SitemapHelper {
  static getSiteUrl(country?: string, language?: string) {
    if (!country || !language) {
      return process.env.NEXT_PUBLIC_MAIN_CANONICAL;
    }

    return `${
      process.env.NEXT_PUBLIC_MAIN_CANONICAL
    }/${country.toLowerCase()}/${language.toLowerCase()}`;
  }

  static mapMediaToSitemapField(
    mediaList: IMediaModel[],
    country?: string,
    language?: string
  ): ISitemapField[] {
    const siteUrl = this.getSiteUrl(country, language);

    return mediaList.map((media) => {
      const path = RouteHelper.getDetailsPath(media);

      return {
        loc: `${siteUrl}${path}`,
        lastmod: media.Updated,
        priority: 1,
        changefreq: "weekly" as ISitemapField["changefreq"],
      };
    });
  }

  static async getAssets(
    assetType: AssetType.Series | AssetType.Movie | AssetType.Collection
  ): Promise<IMediaModel[]> {
    const totalPages = LIST_SIZE / MAX_PAGE_SIZE;
    const requests = [];

    for (let page = 1; page <= totalPages; page++) {
      requests.push(
        DataProvider.getAssetList({
          assetType,
          includeFields: "assetId,type,changed",
          sort: AssetSortType.Popular,
          pageSize: MAX_PAGE_SIZE,
          pageNumber: page,
        })
      );
    }

    const results = await Promise.all(requests);
    return results.flat();
  }

  static async getSitemapFields(
    assetType: AssetType.Series | AssetType.Movie | AssetType.Collection,
    country: string,
    language: string
  ): Promise<ISitemapField[]> {
    const mediaList = await this.getAssets(assetType);
    return this.mapMediaToSitemapField(mediaList, country, language);
  }

  static async getAllCountriesSitemapFields(
    assetTypes: (AssetType.Series | AssetType.Movie | AssetType.Collection)[]
  ): Promise<ISitemapField[]> {
    const results: ISitemapField[] = [];

    for (const country of VALID_COUNTRY) {
      const primaryLanguage = getPrimaryLanguage(country);

      DataProvider.init({
        language: primaryLanguage,
        country: country,
      });

      for (const assetType of assetTypes) {
        const fields = await this.getSitemapFields(
          assetType,
          country,
          primaryLanguage
        );
        results.push(...fields);
      }
    }

    return results;
  }
}
